import {AligningGuidelines} from '@postermywall/fabricjs-2/extensions';
import {noop} from '@Utils/general.util';
import {ActiveSelection, Canvas, type FabricObject} from '@postermywall/fabricjs-2';
import type {Page} from './page.class';
import Emitter from '@/services/event-emitter';
import {initCenteringGuidelines} from '@Libraries/centering-guide-lines-library';
import {GUIDELINES_COLOR, GUIDELINES_MARGIN} from '@PosterWhiteboard/page/page.types';
import {initCanvasEdgeGuidelines} from '@Libraries/canvas-edge-guidelines';

/**
 * The stock extension clears contextTop on every before:render, but the in-progress
 * free-drawing stroke lives on contextTop until mouse-up — any re-render mid-stroke
 * (e.g. the mouse:out render when the cursor crosses the canvas edge) would wipe it.
 */
class DrawingAwareAligningGuidelines extends AligningGuidelines {
  public override beforeRender(): void {
    if (this.canvas.isDrawingMode) {
      return;
    }
    super.beforeRender();
  }
}

export class RenderAlignmentGuidelines {
  public page: Page;
  public disableGuides: (typeof noop)[] = [];
  private isDisposed = false;
  private readonly boundOnAreAlignmentGuidesEnabledUpdated: () => void;

  constructor(page: Page) {
    this.page = page;
    this.boundOnAreAlignmentGuidesEnabledUpdated = this.onAreAlignmentGuidesEnabledUpdated.bind(this);
    Emitter.on('userPreferences:areAlignmentGuidesEnabledUpdated', this.boundOnAreAlignmentGuidesEnabledUpdated);
    if (window.PMW.redux.store.getState().userPreferences.areAlignmentGuidesEnabled) {
      this.enableAligningGuidelines();
    }
  }

  public dispose(): void {
    this.isDisposed = true;
    Emitter.off('userPreferences:areAlignmentGuidesEnabledUpdated', this.boundOnAreAlignmentGuidesEnabledUpdated);
    this.disableAligningGuidelines();
  }

  private areAlignmentGuidesInitialized(): boolean {
    return this.disableGuides.length > 0;
  }

  private onAreAlignmentGuidesEnabledUpdated(): void {
    if (this.isDisposed) {
      return;
    }
    if (window.PMW.redux.store.getState().userPreferences.areAlignmentGuidesEnabled) {
      this.enableAligningGuidelines();
    } else {
      this.disableAligningGuidelines();
    }
  }

  public enableAligningGuidelines(): void {
    if (this.isDisposed || this.areAlignmentGuidesInitialized()) {
      return;
    }

    if (this.page.fabricCanvas instanceof Canvas) {
      const aligningGuidelines = new DrawingAwareAligningGuidelines(this.page.fabricCanvas, {
        color: GUIDELINES_COLOR,
        margin: GUIDELINES_MARGIN,
        getObjectsByTarget: this.getObjectsByTarget.bind(this),
        drawX: noop,
        lineDash: [5, 3],
      });
      this.disableGuides.push(aligningGuidelines.dispose.bind(aligningGuidelines));
      this.disableGuides.push(initCenteringGuidelines(this.page.fabricCanvas));
      this.disableGuides.push(initCanvasEdgeGuidelines(this.page.fabricCanvas));
    }
  }

  private getObjectsByTarget(target: FabricObject): Set<FabricObject> {
    const objects = new Set<FabricObject>();
    const canvas = target.canvas;
    if (!canvas) return objects;
    const children = target instanceof ActiveSelection ? target.getObjects() : [target];

    canvas.forEachObject((o) => {
      if (!o.isOnScreen()) return;
      if (!o.visible) return;
      if (!o.__PMWID) return;
      objects.add(o);
    });

    this.deleteObjectsByList(objects, children);
    return objects;
  }

  private deleteObjectsByList(objects: Set<FabricObject>, list: FabricObject[]): void {
    for (const target of list) {
      objects.delete(target);
    }
  }

  public disableAligningGuidelines(): void {
    for (const disableGuide of this.disableGuides) {
      disableGuide();
    }
    this.disableGuides = [];
  }
}
